import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator as CalculatorIcon, AlertTriangle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import SEOHead from '../components/SEOHead';

// A payout (settlement) figure estimate. The exact, binding number can only come
// from the lender, so this is deliberately an ESTIMATE: outstanding balance plus
// interest accrued since the last payment, plus any early-termination fee.
const CarLoanPayoutCalculator: React.FC = () => {
  const [balance, setBalance] = useState('18000');
  const [interestRate, setInterestRate] = useState('8.00');
  const [daysSincePayment, setDaysSincePayment] = useState('15');
  const [exitFee, setExitFee] = useState('350');

  const [res, setRes] = useState<null | { accrued: number; payout: number }>(null);

  useEffect(() => {
    const bal = parseFloat(balance);
    const rate = parseFloat(interestRate);
    const days = parseFloat(daysSincePayment || '0');
    const fee = parseFloat(exitFee || '0');
    if (isNaN(bal) || bal <= 0 || isNaN(rate) || rate < 0 || isNaN(days) || days < 0 || isNaN(fee) || fee < 0) {
      setRes(null);
      return;
    }
    const dailyRate = rate / 100 / 365;
    const accrued = bal * dailyRate * days;
    setRes({ accrued, payout: bal + accrued + fee });
  }, [balance, interestRate, daysSincePayment, exitFee]);

  const fmt = (v: number): string =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(v);

  return (
    <>
      <SEOHead
        title="Car Loan Payout Calculator Australia | Estimate Your Settlement Figure"
        description="Estimate the payout (settlement) figure to close your car loan early. Adds interest accrued since your last payment and any exit fee to your balance — the only binding figure is your lender's."
        keywords="car loan payout calculator australia, car loan settlement figure, pay out car loan early, car loan payout estimate, early termination car loan"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Car Loan Payout Calculator",
              "description": "Estimate the settlement figure to pay out a car loan early in Australia, including accrued interest and exit fees.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": { "@type": "Country", "name": "Australia" },
              "interactivityType": "active"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Car Loan Payout Calculator"
        description="Estimate the settlement figure to pay out your car loan early — your current balance plus interest accrued since your last payment and any exit fee."
        icon={CalculatorIcon}
        result={res ? `Estimated payout: ${fmt(res.payout)}` : undefined}
      >
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
              <p className="text-sm text-yellow-800">
                This is an <strong>estimate only</strong>. The exact payout is set by your lender and may include break costs, fee
                rebates or adjustments this tool can't know. Always request a formal payout figure before paying out or refinancing.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Input label="Current Loan Balance" value={balance} onChange={setBalance} unit="$" placeholder="e.g. 18000" />
            <Input label="Interest Rate (p.a.)" value={interestRate} onChange={setInterestRate} unit="%" placeholder="e.g. 8.00" />
            <Input label="Days Since Last Payment" value={daysSincePayment} onChange={setDaysSincePayment} unit="days" placeholder="e.g. 15" />
            <Input label="Early Exit / Discharge Fee" value={exitFee} onChange={setExitFee} unit="$" placeholder="e.g. 350" />
          </div>

          {res && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-6 py-4">
                <h2 className="text-xl font-semibold">Estimated Payout Figure</h2>
              </div>
              <div className="p-6">
                <div className="flex justify-between border-b py-2"><span className="text-gray-600">Current balance</span><span className="font-medium">{fmt(parseFloat(balance))}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-600">Interest accrued since last payment</span><span className="font-medium">{fmt(res.accrued)}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-600">Exit / discharge fee</span><span className="font-medium">{fmt(parseFloat(exitFee || '0'))}</span></div>
                <div className="flex justify-between py-3 text-lg"><span className="font-semibold text-gray-800">Estimated payout</span><span className="font-bold text-blue-700">{fmt(res.payout)}</span></div>
              </div>
            </div>
          )}

          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">Frequently asked questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">What is a payout figure?</h3>
                <p className="text-gray-600">It's the amount needed to fully close your loan on a given day — your outstanding balance plus interest accrued to that date, plus any early-termination or discharge fees, less any rebates.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Why can't a calculator give the exact number?</h3>
                <p className="text-gray-600">Lenders apply their own break-cost formulas, fee structures and daily interest conventions. Only your lender's written payout figure is binding — treat this as a planning estimate.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Should I pay out or refinance?</h3>
                <p className="text-gray-600">If you're switching lenders, use the payout figure as the balance in our{' '}
                  <Link className="text-blue-600 hover:underline" to="/car-refinance-calculator/">refinance calculator</Link>. If you're paying it off from savings, our{' '}
                  <Link className="text-blue-600 hover:underline" to="/early-payoff-calculator/">early payoff calculator</Link> shows the interest saved.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Are there penalties for paying out early?</h3>
                <p className="text-gray-600">Some loans charge early termination or break fees. Check your contract and ask the lender to itemise the payout.</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 pt-2">Estimates only and not financial advice. Interest is estimated on a simple daily-accrual basis and excludes lender-specific break costs. Always obtain a formal payout figure from your lender.</p>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default CarLoanPayoutCalculator;
